import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, Calendar, Send, Info, Loader2 } from 'lucide-react';
import { QuizState, FitResult } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useConsent } from '../contexts/ConsentContext';

const INITIAL_STATE: QuizState = {
  step: 0,
  incomeTypes: [],
  legalForm: null,
  industryFailed: null,
  revenue: null,
  result: null,
  seenPackages: null,
  rejectReason: undefined
};

const parsePrice = (priceStr: any): number | null => {
  if (typeof priceStr === 'number') return priceStr;
  if (!priceStr) return null;
  const cleanStr = priceStr.toString().replace(/[^0-9]/g, '');
  const num = parseInt(cleanStr, 10);
  return isNaN(num) ? null : num;
};

const getQuizPackagesSubtext = (pricingData: any, locale: 'EN' | 'DE'): string => {
  const defaultDe = 'Wir bieten derzeit ausschließlich Jahrespakete und keine einmaligen Beratungen an.';
  const defaultEn = "We currently only offer annual packages and don't have capacity for one off services.";

  if (!pricingData) {
    return locale === 'DE' ? defaultDe : defaultEn;
  }

  const pricesList: number[] = [];
  const keys = ['starter', 'standard', 'pro'];
  keys.forEach(k => {
    const pkg = pricingData[k];
    if (pkg && pkg.price !== undefined && pkg.price !== null) {
      const parsed = parsePrice(pkg.price);
      if (parsed !== null) {
        pricesList.push(parsed);
      }
    }
  });

  if (pricesList.length === 0) {
    return locale === 'DE' ? defaultDe : defaultEn;
  }

  const lowest = Math.min(...pricesList);
  const highest = Math.max(...pricesList);

  const formatNumber = (val: number): string => {
    const parts = val.toString().split('');
    let formatted = '';
    let count = 0;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (count > 0 && count % 3 === 0) {
        formatted = '.' + formatted;
      }
      formatted = parts[i] + formatted;
      count++;
    }
    return formatted;
  };

  if (lowest === highest) {
    return locale === 'DE'
      ? `Wir bieten derzeit ausschließlich Jahrespakete und keine einmaligen Beratungen an. Die Pakete starten bei € ${formatNumber(lowest)} pro Jahr.`
      : `We currently only offer annual packages and don't have capacity for one off services. Package prices start at € ${formatNumber(lowest)} / year.`;
  }

  return locale === 'DE'
    ? `Wir bieten derzeit ausschließlich Jahrespakete und keine einmaligen Beratungen an. Die Pakete reichen von € ${formatNumber(lowest)}-${formatNumber(highest)} pro Jahr.`
    : `We currently only offer annual packages and don't have capacity for one off services. Package prices range from € ${formatNumber(lowest)}-${formatNumber(highest)} / year.`;
};

export const Quiz: React.FC = () => {
  const [state, setState] = useState<QuizState>(INITIAL_STATE);
  const { t, language } = useLanguage();
  const qt = t.fitCheck;
  const { consentState, openSettings, enableBookingConsent } = useConsent();

  const [shouldLoadCalendly, setShouldLoadCalendly] = useState(false);
  const [isCalendlyReady, setIsCalendlyReady] = useState(false);
  // Set the moment the user clicks "Terminbuchung aktivieren" so the button can
  // show a loading state instead of looking dead while consent flips and the
  // Calendly iframe mounts (Clarity showed a "dead click then click" here).
  const [activatingBooking, setActivatingBooking] = useState(false);

  // consentState.marketing can already be true-from-a-previous-visit by the
  // time this component hydrates (ConsentContext reads localStorage in its own
  // effect, which can fire before this component's turn during concurrent
  // hydration) — the server never knows about it, so branching on it directly
  // caused a hydration mismatch (React error #418) on reload. Gating on our
  // own mount state guarantees the first render here always matches the
  // server (hasMounted is false on both), and only switches after this
  // component's own hydration is guaranteed complete.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Once booking consent is granted from the inline card, bring the freshly
  // mounted Calendly widget into view so the user doesn't have to hunt for it.
  useEffect(() => {
    if (activatingBooking && consentState.marketing) {
      document
        .getElementById('calendly-preloaded-widget')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activatingBooking, consentState.marketing]);

  // GTM Tracking Refs to ensure events are sent exactly once
  const hasSentStartRef = React.useRef(false);
  const hasSentCompleteRef = React.useRef(false);
  const hasSentQualifiedRef = React.useRef(false);
  const hasSentCalendlyViewRef = React.useRef(false);
  const hasSentBookingRef = React.useRef(false);

  const langStr = language ? language.toLowerCase() : 'unknown';

  const triggerFitCheckStart = () => {
    if (!hasSentStartRef.current) {
      hasSentStartRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'fit_check_start',
        page_path: window.location.pathname,
        language: langStr
      });
    }
    // Pre-warm the Calendly widget in the background as soon as the user starts
    setShouldLoadCalendly(true);
  };

  const triggerStepComplete = (stepNum: number) => {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: 'fit_check_step_complete',
      step_number: stepNum,
      step_result: 'passed',
      page_path: window.location.pathname,
      language: langStr
    });
  };

  const triggerStepFailed = (stepNum: number, reason: string) => {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: 'fit_check_step_failed',
      step_number: stepNum,
      step_result: 'failed',
      failure_reason: reason,
      page_path: window.location.pathname,
      language: langStr
    });
  };

  const triggerFitCheckComplete = () => {
    if (!hasSentCompleteRef.current) {
      hasSentCompleteRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'fit_check_complete',
        fit_check_result: 'qualified',
        page_path: window.location.pathname,
        language: langStr
      });
    }
  };

  const triggerFitCheckQualified = () => {
    if (!hasSentQualifiedRef.current) {
      hasSentQualifiedRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'fit_check_qualified',
        page_path: window.location.pathname,
        language: langStr
      });
    }
  };

  const langStrRef = React.useRef(langStr);
  useEffect(() => {
    langStrRef.current = langStr;
  }, [langStr]);

  const triggerCalendlyView = () => {
    if (!hasSentCalendlyViewRef.current) {
      hasSentCalendlyViewRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'calendly_view',
        page_path: window.location.pathname,
        language: langStrRef.current
      });
    }
  };

  const triggerCalendlyBookingComplete = React.useCallback(() => {
    if (!hasSentBookingRef.current) {
      hasSentBookingRef.current = true;
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'calendly_booking_complete',
        page_path: window.location.pathname,
        language: langStrRef.current
      });
      console.log('[GTM] calendly_booking_complete event pushed to dataLayer', {
        event: 'calendly_booking_complete',
        page_path: window.location.pathname,
        language: langStrRef.current
      });

      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'conversion', {
          'send_to': 'AW-18326930745/vIJQCNrm2tUcELmK-6JE',
          'value': 1.0,
          'currency': 'EUR'
        });
      }
    }
  }, []);

  useEffect(() => {
    const handleCalendlyEvent = (e: MessageEvent) => {
      console.log('[Calendly Debug] raw message', { origin: e.origin, data: e.data });
      let data: any = e.data;

      // Handle stringified JSON data from postMessage
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          // Non-JSON string message
        }
      }

      if (!data) return;

      const eventName = typeof data === 'object' ? (data.event || data.action || data.type) : null;
      const isFromCalendlyOrigin = typeof e.origin === 'string' && e.origin.includes('calendly.com');
      const isCalendlyEvent = isFromCalendlyOrigin || (typeof eventName === 'string' && eventName.toLowerCase().includes('calendly'));

      if (isCalendlyEvent) {
        setIsCalendlyReady(true);

        const isScheduled = 
          eventName === 'calendly.event_scheduled' ||
          eventName === 'event_scheduled' ||
          (typeof eventName === 'string' && eventName.toLowerCase().includes('event_scheduled'));

        if (isScheduled) {
          triggerCalendlyBookingComplete();
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);
    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [triggerCalendlyBookingComplete]);

  const handleNext = () => {
    if (state.step === 0) {
      setShouldLoadCalendly(true);
      triggerFitCheckStart(); // Fallback if they clicked Next without toggle (though Next is disabled if none selected)
      const hasSelfEmployment = state.incomeTypes.includes('self');
      if (!hasSelfEmployment) {
        triggerStepFailed(1, 'income_type_not_self_employed');
        setState(prev => ({ ...prev, result: 'reject', rejectReason: 'income' }));
        return;
      }
      triggerStepComplete(1);
    }
    
    if (state.step === 1) {
      if (state.legalForm === 'company') {
        triggerStepFailed(2, 'not_eligible');
        setState(prev => ({ ...prev, result: 'reject', rejectReason: 'legal' }));
        return;
      }
      triggerStepComplete(2);
    }

    if (state.step === 2) {
      if (state.industryFailed) {
        triggerStepFailed(3, 'insufficient_fit');
        setState(prev => ({ ...prev, result: 'reject', rejectReason: 'industry' }));
        return;
      }

      if (state.industryFailed === false) {
        triggerStepComplete(3);
      }
    }

    if (state.step === 3) {
      triggerStepComplete(4);
      triggerFitCheckComplete();
      triggerFitCheckQualified();
      setShouldLoadCalendly(true);
      setState(prev => ({ ...prev, result: 'good' }));
      return;
    }

    setState(prev => ({ ...prev, step: prev.step + 1 }));
  };

  const handleBack = () => setState(prev => ({ ...prev, step: Math.max(0, prev.step - 1) }));

  const handleRestart = () => {
    hasSentBookingRef.current = false;
    hasSentCalendlyViewRef.current = false;
    setState(INITIAL_STATE);
    const section = document.getElementById('fit-check');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleIncome = (key: string) => {
    setShouldLoadCalendly(true);
    triggerFitCheckStart();
    setState(prev => {
      const isSelected = prev.incomeTypes.includes(key);
      return {
        ...prev,
        incomeTypes: isSelected 
          ? prev.incomeTypes.filter(t => t !== key)
          : [...prev.incomeTypes, key]
      };
    });
  };

  const currentStepData = qt.steps[state.step];
  const stepTitleRef = React.useRef<HTMLHeadingElement>(null);
  const isFirstRender = React.useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (state.result === null) {
      stepTitleRef.current?.focus({ preventScroll: true });
    }
  }, [state.step, state.result]);

  // Calendly warm-up as soon as marketing consent exists (banner, settings or a
  // consent saved earlier): preconnect + widget.js, so the embed is quick once
  // the visitor reaches it. Never before consent (GDPR) — index.html no longer
  // loads widget.js up front.
  useEffect(() => {
    if (!consentState.marketing) return;
    for (const href of ['https://calendly.com', 'https://assets.calendly.com']) {
      if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) continue;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
    if (!(window as any).Calendly && !document.querySelector('script[src*="calendly.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, [consentState.marketing]);

  // Dynamically load/init Calendly widget in the background as soon as shouldLoadCalendly is true
  const hasInitializedCalendlyRef = React.useRef<string | null>(null);

  const getCalendlyUrlWithUtm = (baseUrl: string): string => {
    try {
      const url = new URL(baseUrl);
      const hostname = window.location.hostname.toLowerCase();
      
      // Determine campaign based on domain: pstax.at gets "main-site", devs.pstax.at gets "devs-site"
      const campaign = (hostname === 'pstax.at' || hostname === 'www.pstax.at')
        ? 'main-site'
        : 'devs-site';

      url.searchParams.set('utm_source', 'website');
      url.searchParams.set('utm_medium', 'referral');
      url.searchParams.set('utm_campaign', campaign);

      return url.toString();
    } catch {
      const hostname = window.location.hostname.toLowerCase();
      const campaign = (hostname === 'pstax.at' || hostname === 'www.pstax.at') ? 'main-site' : 'devs-site';
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}utm_source=website&utm_medium=referral&utm_campaign=${campaign}`;
    }
  };

  useEffect(() => {
    if (!shouldLoadCalendly || !consentState.marketing) return;

    let timeoutId: number;

    const initCalendly = () => {
      const widgetContainer = document.getElementById('calendly-preloaded-widget');
      const CalendlyObj = (window as any).Calendly;
      const targetUrl = getCalendlyUrlWithUtm(qt.calendly);

      if (CalendlyObj && widgetContainer) {
        const existingIframe = widgetContainer.querySelector('iframe');
        if (existingIframe && hasInitializedCalendlyRef.current === targetUrl) {
          return;
        }

        widgetContainer.innerHTML = '';
        CalendlyObj.initInlineWidget({
          url: targetUrl,
          parentElement: widgetContainer,
        });
        hasInitializedCalendlyRef.current = targetUrl;

        // Monitor loading via iframe onload
        setTimeout(() => {
          const iframe = widgetContainer.querySelector('iframe');
          if (iframe) {
            iframe.addEventListener('load', () => {
              setIsCalendlyReady(true);
            });
          }
        }, 100);

      } else {
        // Ensure Calendly script/styles are present
        if (!document.querySelector('script[src*="calendly.com"]')) {
          const script = document.createElement('script');
          script.src = 'https://assets.calendly.com/assets/external/widget.js';
          script.async = true;
          document.head.appendChild(script);
        }
        if (!document.querySelector('link[href*="calendly.com"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://assets.calendly.com/assets/external/widget.css';
          document.head.appendChild(link);
        }
        timeoutId = window.setTimeout(initCalendly, 100);
      }
    };

    initCalendly();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [shouldLoadCalendly, qt.calendly, consentState.marketing]);

  // Trigger 'calendly_view' analytics event ONLY when the user reaches the booking step
  useEffect(() => {
    if (state.result === 'good') {
      triggerCalendlyView();
    }
  }, [state.result]);

  return (
    <section id="fit-check" className="py-12 bg-pstax-bg scroll-mt-20 overflow-x-hidden">
      <div className="container mx-auto px-6">
        <div className={state.result === 'good' ? "max-w-7xl mx-auto" : "max-w-6xl mx-auto"}>
          <div className={`bg-white rounded-2xl border border-pstax-border shadow-2xl flex flex-col transition-all duration-300 ${state.result === 'good' ? 'min-h-[1100px]' : 'min-h-[600px] overflow-hidden'}`}>
            
            {/* Header & Progress (Hidden on Result Page) */}
            <AnimatePresence>
              {state.result === null && (
                <m.div 
                  initial={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-6 md:px-10 py-6 border-b border-pstax-border bg-pstax-bg/50"
                  role="region"
                  aria-label="Quiz progress"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                    <h2 className="text-xl font-display font-bold">{qt.header}</h2>
                    <div className="text-[10px] font-semibold text-pstax-gray uppercase tracking-widest" aria-hidden="true">
                      {qt.stepLabel} {state.step + 1} {qt.ofLabel} {qt.steps.length}
                    </div>
                  </div>
                  
                  <div 
                    className="flex gap-2" 
                    role="progressbar" 
                    aria-valuemin={0} 
                    aria-valuemax={qt.steps.length} 
                    aria-valuenow={state.step + 1}
                    aria-label={`${qt.stepLabel} ${state.step + 1} ${qt.ofLabel} ${qt.steps.length}`}
                  >
                    {qt.steps.map((_: any, i: number) => (
                      <div key={i} className="flex-1 h-2 rounded-full bg-pstax-border overflow-hidden">
                        <m.div 
                          initial={false}
                          animate={{ width: state.step >= i ? '100%' : '0%' }}
                          className="h-full bg-pstax-blue"
                        />
                      </div>
                    ))}
                  </div>
                </m.div>
              )}
            </AnimatePresence>

            {/* Content */}
            <div 
              className={`flex-1 flex flex-col ${state.result === 'good' ? 'px-0 pt-4 pb-0' : 'px-6 md:px-10 py-8 min-h-[400px]'}`}
              aria-live="polite"
              role="main"
            >
              <AnimatePresence mode="wait">
                {state.result === null && (
                  <m.div
                    key="quiz-steps"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="h-full max-w-3xl mx-auto w-full"
                  >
                    <div className="mb-6">
                      <h3 
                        ref={stepTitleRef}
                        tabIndex={-1}
                        className="text-xl font-display font-bold mb-1 focus:outline-none animate-fadeIn" 
                        id="quiz-step-title"
                      >
                        {currentStepData.id === 'packages' ? (
                          language === 'DE' ? (
                            <>
                              Hast du{' '}
                              <a 
                                href="#pricing" 
                                className="text-black underline font-extrabold"
                                onClick={(e) => {
                                  const pricingSection = document.getElementById('pricing');
                                  if (pricingSection) {
                                    e.preventDefault();
                                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                                  }
                                }}
                              >
                                unsere Pakete
                              </a>{' '}
                              gesehen?
                            </>
                          ) : (
                            <>
                              Did you see{' '}
                              <a 
                                href="#pricing" 
                                className="text-black underline font-extrabold"
                                onClick={(e) => {
                                  const pricingSection = document.getElementById('pricing');
                                  if (pricingSection) {
                                    e.preventDefault();
                                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                                  }
                                }}
                              >
                                our packages
                              </a>
                              ?
                            </>
                          )
                        ) : (
                          currentStepData.title
                        )}
                      </h3>
                      <p className="text-lg text-pstax-gray animate-fadeIn" id="quiz-step-question">
                        {currentStepData.id === 'packages' ? (
                          getQuizPackagesSubtext(t.pricing, language)
                        ) : (
                          currentStepData.question
                        )}
                      </p>
                    </div>
                    
                    <div className="grid gap-2" role="group" aria-labelledby="quiz-step-question">
                      {state.step === 0 ? (
                        <div className="space-y-2">
                          {currentStepData.options?.map((opt: any) => (
                            <button
                              key={opt.key}
                              onClick={() => toggleIncome(opt.key)}
                              className={`w-full p-4 text-left rounded-xl border flex items-center justify-between transition-all group ${state.incomeTypes.includes(opt.key) ? 'bg-pstax-blue/10 border-pstax-blue border-2' : 'border-pstax-border hover:border-pstax-blue/40'}`}
                              aria-pressed={state.incomeTypes.includes(opt.key)}
                            >
                              <span className={`flex-1 text-base font-medium mr-4 ${state.incomeTypes.includes(opt.key) ? 'text-pstax-navy' : 'text-pstax-gray group-hover:text-pstax-navy'}`}>
                                {opt.label}
                              </span>
                              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${state.incomeTypes.includes(opt.key) ? 'bg-pstax-blue border-pstax-blue text-white' : 'border-pstax-border'}`} aria-hidden="true">
                                {state.incomeTypes.includes(opt.key) && <CheckCircle2 size={14} />}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : state.step === 2 ? (
                        <div className="space-y-4">
                          <div className="grid gap-3">
                             <button
                               onClick={() => setState(prev => ({ ...prev, industryFailed: true }))}
                               className={`w-full p-4 text-left rounded-xl border flex items-center justify-between transition-all group ${state.industryFailed === true ? 'bg-red-50 border-red-500 border-2' : 'border-pstax-border hover:border-red-200'}`}
                               aria-pressed={state.industryFailed === true}
                             >
                               <div className="flex-1">
                                 <span className={`text-base font-medium block mb-0.5 ${state.industryFailed === true ? 'text-red-700' : 'text-pstax-gray group-hover:text-pstax-navy'}`}>
                                   {currentStepData.yes}
                                 </span>
                                 <p className="text-xs text-pstax-gray opacity-80 leading-tight">
                                   {currentStepData.options?.join(', ')}
                                 </p>
                                </div>
                               <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${state.industryFailed === true ? 'bg-red-500 border-red-500 text-white' : 'border-pstax-border'}`} aria-hidden="true">
                                 {state.industryFailed === true && <AlertCircle size={14} />}
                               </div>
                             </button>
                             <button
                                onClick={() => setState(prev => ({ ...prev, industryFailed: false }))}
                                className={`w-full p-4 text-left rounded-xl border flex items-center justify-between transition-all group ${state.industryFailed === false ? 'bg-green-50 border-green-500 border-2' : 'border-pstax-border hover:border-pstax-blue/40'}`}
                                aria-pressed={state.industryFailed === false}
                              >
                                <span className={`text-base font-medium ${state.industryFailed === false ? 'text-green-700' : 'text-pstax-gray group-hover:text-pstax-navy'}`}>
                                  {currentStepData.no}
                                </span>
                                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${state.industryFailed === false ? 'bg-green-500 border-green-500 text-white' : 'border-pstax-border'}`} aria-hidden="true">
                                  {state.industryFailed === false && <CheckCircle2 size={14} />}
                                </div>
                              </button>
                          </div>
                        </div>
                      ) : state.step === 3 ? (
                        <div className="space-y-2">
                          {currentStepData.options?.map((opt: any) => {
                            const isSelected = state.seenPackages === true;
                            const borderColor = isSelected ? 'border-green-500 border-2 bg-green-50' : 'border-pstax-border hover:border-pstax-blue/40';
                            const textColor = isSelected ? 'text-green-700' : 'text-pstax-gray hover:text-pstax-navy';
                            const indicatorBg = isSelected ? 'bg-green-500 border-green-500 text-white' : 'bg-white text-white border-pstax-border';

                            return (
                              <button
                                key={opt.key}
                                onClick={() => setState(prev => ({ ...prev, seenPackages: true }))}
                                className={`w-full p-4 text-left rounded-xl border flex items-center justify-between transition-all group ${borderColor}`}
                                aria-pressed={isSelected}
                              >
                                <span className={`flex-1 text-base font-medium mr-4 ${textColor}`}>
                                  {opt.label}
                                </span>
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${indicatorBg}`} aria-hidden="true">
                                  {isSelected && <CheckCircle2 size={14} />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        currentStepData.options?.map((opt: any) => {
                          const isSelected = state.legalForm === opt.key;
                          let borderColor = 'border-pstax-border hover:border-pstax-blue/40';
                          let bgColor = '';
                          let textColor = 'text-pstax-gray group-hover:text-pstax-navy';
                          let indicatorBg = 'bg-white text-white border-pstax-border';

                          if (isSelected) {
                            const isWrong = opt.key === 'company';
                            const isRight = opt.key === 'solo';
                            
                            if (isWrong) {
                              borderColor = 'border-red-500 border-2';
                              bgColor = 'bg-red-50';
                              textColor = 'text-red-700';
                              indicatorBg = 'bg-red-500 border-red-500 text-white';
                            } else if (isRight) {
                              borderColor = 'border-green-500 border-2';
                              bgColor = 'bg-green-50';
                              textColor = 'text-green-700';
                              indicatorBg = 'bg-green-500 border-green-500 text-white';
                            } else {
                              borderColor = 'border-pstax-blue border-2';
                              bgColor = 'bg-pstax-blue/10';
                              textColor = 'text-pstax-navy';
                              indicatorBg = 'bg-pstax-blue border-pstax-blue text-white';
                            }
                          }

                          return (
                            <button
                              key={opt.key}
                              onClick={() => setState(prev => ({ ...prev, legalForm: opt.key }))}
                              className={`p-4 text-left rounded-xl border flex items-center justify-between transition-all group ${bgColor} ${borderColor}`}
                              aria-pressed={isSelected}
                            >
                              <span className={`flex-1 text-base font-medium mr-4 ${textColor}`}>
                                {opt.label}
                              </span>
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${indicatorBg}`} aria-hidden="true">
                                {isSelected && (opt.key === 'company' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />)}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </m.div>
                )}

                {state.result === 'reject' && (
                  <m.div
                    key="rejection"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    className="text-center py-8 px-4 flex-1 flex flex-col items-center justify-center w-full"
                    role="alert"
                    aria-live="assertive"
                  >
                    <AlertCircle size={64} className="text-red-500 mb-6" aria-hidden="true" />
                    <h3 className="text-2xl font-display font-bold mb-4">{qt.rejectionLabel}</h3>
                    <p className="text-base text-pstax-gray mb-8 max-w-xl mx-auto leading-relaxed">
                      {state.rejectReason ? qt.reasons[state.rejectReason] : qt.reasons.default}
                    </p>
                    
                    <div className="p-8 bg-pstax-bg rounded-2xl border border-pstax-border text-left max-w-lg w-full">
                      <p className="font-bold text-pstax-navy text-sm mb-2">{qt.rejectionMistake}</p>
                      <p className="text-xs text-pstax-gray mb-6 leading-relaxed">
                        {qt.rejectionEmail
                          .split('office@pstax.at')
                          .flatMap((part: string, i: number) =>
                            i === 0
                              ? [part]
                              : [
                                  <a
                                    key={i}
                                    href="mailto:office@pstax.at"
                                    className="text-pstax-blue font-bold underline underline-offset-2"
                                  >
                                    office@pstax.at
                                  </a>,
                                  part,
                                ]
                          )}
                      </p>
                      <div className="flex flex-wrap gap-4 items-center justify-between">
                        <a href="mailto:office@pstax.at" className="btn-primary py-2 px-6" aria-label={qt.emailUs}>{qt.emailUs}</a>
                        <button 
                          onClick={handleRestart}
                          className="text-pstax-blue text-sm font-bold flex items-center gap-2 hover:translate-x-1 transition-transform"
                          aria-label={qt.retry}
                        >
                          <ChevronLeft size={16} aria-hidden="true" />
                          {qt.retry}
                        </button>
                      </div>
                    </div>
                  </m.div>
                )}
              </AnimatePresence>

              {/* Success View: Stable container for Calendly pre-loading */}
              <div className={state.result === 'good' ? 'block' : 'hidden'}>
                <m.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={state.result === 'good' ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                  className="flex flex-col w-full items-center pt-2"
                >
                  <div className="text-center mb-2 px-6 md:px-10">
                    <div className="flex flex-col md:flex-row items-center justify-center gap-3 mb-1">
                      <CheckCircle2 size={28} className="text-green-500" aria-hidden="true" />
                      <h3 className="text-xl font-display font-bold">{qt.good.headline}</h3>
                    </div>
                    <p className="text-base text-pstax-gray max-w-lg mx-auto">{qt.good.bookText}</p>
                  </div>
                  
                  <div className="w-full border-t border-pstax-border bg-white relative mt-0 overflow-hidden">
                    {hasMounted && consentState.marketing ? (
                      <div className="relative w-full" style={{ minHeight: '950px' }}>
                        {/* Loading Skeleton */}
                        {!isCalendlyReady && (
                          <div 
                            className="absolute inset-0 bg-white flex flex-col items-center justify-start pt-16 z-10 animate-pulse"
                            id="calendly-skeleton"
                          >
                            <div className="w-11/12 max-w-4xl h-8 bg-gray-100 rounded-lg mb-8" />
                            <div className="w-11/12 max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8 h-[600px] w-full px-4">
                              <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 flex flex-col justify-between">
                                <div className="space-y-4">
                                  <div className="h-6 bg-gray-200 rounded w-1/3" />
                                  <div className="h-8 bg-gray-200 rounded w-2/3" />
                                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                                </div>
                                <div className="space-y-2">
                                  <div className="h-4 bg-gray-100 rounded w-full" />
                                  <div className="h-4 bg-gray-100 rounded w-11/12" />
                                </div>
                              </div>
                              <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 grid grid-cols-7 gap-2 content-start">
                                {Array.from({ length: 35 }).map((_, i) => (
                                  <div key={i} className="aspect-square bg-gray-200/60 rounded-full" />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        <div 
                          id="calendly-preloaded-widget" 
                          style={{ height: '950px', width: '100%', border: 'none' }}
                          title="Calendly scheduling widget"
                        />
                      </div>
                    ) : (
                      <div className="py-12 sm:py-16 px-6 text-center max-w-lg mx-auto flex flex-col items-center justify-center my-6 sm:my-8 bg-white border border-pstax-border rounded-2xl shadow-sm">
                        <div className="w-12 h-12 bg-pstax-navy/5 text-pstax-navy rounded-full flex items-center justify-center mb-4 border border-pstax-border">
                          <Calendar size={24} className="text-pstax-navy" />
                        </div>
                        <h4 className="text-lg font-display font-bold text-pstax-navy mb-2">
                          {language === 'DE' ? 'Nur noch ein Schritt' : 'One more step'}
                        </h4>
                        <p className="text-xs sm:text-sm text-slate-700 mb-6 leading-relaxed">
                          {language === 'DE' 
                            ? 'Für die Terminbuchung nutzen wir den externen Kalender-Dienst Calendly. Aktiviere Marketing- & externe Medien-Cookies, um deinen Termin direkt hier zu buchen.'
                            : 'We use Calendly for appointment booking. Enable marketing & external media cookies to schedule your call directly here.'
                          }
                        </p>
                        <button
                          type="button"
                          onClick={() => { setActivatingBooking(true); enableBookingConsent(); }}
                          disabled={activatingBooking}
                          className="btn-primary py-3 px-8 text-sm font-bold shadow-md shadow-pstax-navy/10 w-full sm:w-auto mb-3 hover:bg-pstax-navy/90 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-wait inline-flex items-center justify-center gap-2"
                          id="enable-booking-inline-btn"
                        >
                          {activatingBooking && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                          {activatingBooking
                            ? (language === 'DE' ? 'Wird geladen …' : 'Loading …')
                            : (language === 'DE' ? 'Terminbuchung aktivieren' : 'Enable booking')}
                        </button>
                        <button
                          type="button"
                          onClick={openSettings}
                          className="text-xs text-slate-600 hover:text-pstax-navy font-semibold underline underline-offset-2 transition-colors"
                          id="open-cookie-settings-inline-btn"
                        >
                          {language === 'DE' ? 'Cookie-Einstellungen anpassen' : 'Adjust cookie preferences'}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-6 text-center px-6 md:px-10 pb-8">
                    <button 
                      onClick={handleRestart}
                      className="text-pstax-blue text-sm font-bold flex items-center gap-2 hover:translate-x-1 transition-transform"
                      aria-label={qt.startOver}
                    >
                      <ChevronLeft size={18} aria-hidden="true" />
                      {qt.startOver}
                    </button>
                  </div>
                </m.div>
              </div>
            </div>

            {/* Footer Navigation (Hidden on Result Page) */}
            {state.result === null && (
              <div className="px-6 md:px-10 py-6 bg-pstax-bg/50 border-t border-pstax-border flex items-center justify-between">
                {state.step > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-pstax-gray hover:text-pstax-navy font-bold text-base transition-all"
                  >
                    <ChevronLeft size={20} />
                    {qt.back}
                  </button>
                )}
                
                <div className={state.step === 0 ? 'ml-auto' : ''}>
                  <button
                    onClick={handleNext}
                    disabled={
                      (state.step === 0 && state.incomeTypes.length === 0) ||
                      (state.step === 1 && !state.legalForm) ||
                      (state.step === 2 && state.industryFailed === null) ||
                      (state.step === 3 && state.seenPackages !== true)
                    }
                    className="btn-primary py-3 px-10 flex items-center gap-2 disabled:opacity-50 text-base font-bold shadow-lg shadow-pstax-blue/20"
                  >
                    {state.step === qt.steps.length - 1 ? qt.showResult : qt.next}
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
